import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'

import { acquireApiAuthToken, isEntraConfigured } from './auth/entraClient'

let apolloClientInstance = null

function normalizeEndpoint(endpoint) {
  if (!endpoint) {
    return null
  }

  const value = String(endpoint).trim()
  if (!value) {
    return null
  }

  // Keep explicit GraphQL paths unchanged.
  if (/\/graphql(?:$|[?#])/.test(value)) {
    return value
  }

  return `${value.replace(/\/$/, '')}/graphql`
}

function resolveEndpoint() {
  const configuredEndpoint = normalizeEndpoint(
    (window.config && (window.config.graphqlEndpoint || window.config.api || window.config.graphql))
    || process.env.REACT_APP_GRAPHQL_ENDPOINT
  )

  if (configuredEndpoint) {
    return configuredEndpoint
  }

  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return `${window.location.origin.replace(/\/$/, '')}/graphql`
  }

  return 'http://localhost:4000/graphql'
}

export function createApolloClient() {
  const uri = resolveEndpoint()
  const authLink = setContext(async (_, { headers }) => {
    const nextHeaders = { ...(headers || {}) }

    if (isEntraConfigured()) {
      const token = await acquireApiAuthToken({ interactive: false })
      if (token) {
        nextHeaders.Authorization = `Bearer ${token}`
      }
    }

    if (!nextHeaders.Authorization) {
      try {
        const rawUser = localStorage.getItem('platform-ui.local-auth-user')
        const persistedUser = rawUser ? JSON.parse(rawUser) : null
        if (persistedUser && persistedUser.apiKey) {
          nextHeaders['x-api-key'] = persistedUser.apiKey
          nextHeaders['Ocp-Apim-Subscription-Key'] = persistedUser.apiKey
        }
      } catch (error) {
        // Ignore local storage read errors and continue without auth header.
      }
    }

    return { headers: nextHeaders }
  })

  return new ApolloClient({
    link: ApolloLink.from([authLink, new HttpLink({ uri, credentials: 'include' })]),
    cache: new InMemoryCache(),
  })
}

export function setApolloClient(client) {
  apolloClientInstance = client
}

export function getApolloClient() {
  if (!apolloClientInstance) {
    apolloClientInstance = createApolloClient()
  }

  return apolloClientInstance
}

export default {
  createApolloClient,
  getApolloClient,
  setApolloClient,
}
